import { CircleCheck, CircleX, Info, TriangleAlert, X, type LucideIcon } from 'lucide-react';
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { ApiError, errorMessage, uuid } from '../../lib/api.ts';
import { cx } from '../../lib/cx.ts';
import { useSwipeAway } from '../../lib/useSwipeAway.ts';
import './Toast.css';

export type ToastTone = 'success' | 'danger' | 'warning' | 'info' | 'neutral';

export interface ToastOptions {
  title: string;
  description?: string;
  tone?: ToastTone;
  /** Default 4000ms; 0 keeps the toast until dismissed. */
  durationMs?: number;
  action?: { label: string; onClick: () => void };
}

export interface ToastApi {
  show: (options: ToastOptions) => string;
  success: (title: string, description?: string) => string;
  info: (title: string, description?: string) => string;
  warning: (title: string, description?: string) => string;
  /** Accepts a Thai message or any thrown error (ApiError message + reference id). */
  error: (errorOrTitle: unknown, description?: string) => string;
  dismiss: (id: string) => void;
}

interface ToastRecord extends ToastOptions {
  id: string;
  tone: ToastTone;
  leaving: boolean;
}

const MAX_VISIBLE = 4;
const TONE_ICON: Record<ToastTone, LucideIcon> = {
  success: CircleCheck,
  danger: CircleX,
  warning: TriangleAlert,
  info: Info,
  neutral: Info,
};

const ToastContext = createContext<ToastApi | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastRecord[]>([]);

  const dismiss = useCallback((id: string) => {
    setToasts((list) => list.map((t) => (t.id === id ? { ...t, leaving: true } : t)));
    window.setTimeout(() => setToasts((list) => list.filter((t) => t.id !== id)), 180);
  }, []);

  const show = useCallback((options: ToastOptions) => {
    const id = uuid();
    setToasts((list) => [...list.slice(-(MAX_VISIBLE - 1)), { ...options, id, tone: options.tone ?? 'neutral', leaving: false }]);
    return id;
  }, []);

  const api = useMemo<ToastApi>(
    () => ({
      show,
      dismiss,
      success: (title, description) => show({ title, description, tone: 'success' }),
      info: (title, description) => show({ title, description, tone: 'info' }),
      warning: (title, description) => show({ title, description, tone: 'warning', durationMs: 5000 }),
      error: (errorOrTitle, description) => {
        if (typeof errorOrTitle === 'string') return show({ title: errorOrTitle, description, tone: 'danger', durationMs: 6000 });
        const ref =
          errorOrTitle instanceof ApiError && errorOrTitle.correlationId ? `รหัสอ้างอิง ${errorOrTitle.correlationId.slice(0, 8)}` : undefined;
        return show({ title: errorMessage(errorOrTitle), description: description ?? ref, tone: 'danger', durationMs: 6000 });
      },
    }),
    [show, dismiss],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      {createPortal(
        <ol className="toast-region" aria-live="polite" aria-label="การแจ้งเตือน">
          {toasts.map((t) => (
            <ToastItem key={t.id} toast={t} onDismiss={dismiss} />
          ))}
        </ol>,
        document.body,
      )}
    </ToastContext.Provider>
  );
}

function ToastItem({ toast, onDismiss }: { toast: ToastRecord; onDismiss: (id: string) => void }) {
  const [paused, setPaused] = useState(false);
  const duration = toast.durationMs ?? 4000;

  useEffect(() => {
    if (paused || duration <= 0 || toast.leaving) return;
    const timer = window.setTimeout(() => onDismiss(toast.id), duration);
    return () => window.clearTimeout(timer);
  }, [paused, duration, toast.id, toast.leaving, onDismiss]);

  const swipe = useSwipeAway(() => onDismiss(toast.id));
  const Icon = TONE_ICON[toast.tone];
  return (
    <li
      className={cx('toast', `toast--${toast.tone}`, toast.leaving && 'is-leaving')}
      role={toast.tone === 'danger' ? 'alert' : 'status'}
      {...swipe.handlers}
      style={swipe.style}
      onPointerEnter={() => setPaused(true)}
      onPointerLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
    >
      <Icon className="toast__icon" size={18} strokeWidth={1.75} aria-hidden="true" />
      <div className="toast__body">
        <p className="toast__title">{toast.title}</p>
        {toast.description && <p className="toast__description">{toast.description}</p>}
        {toast.action && (
          <button
            type="button"
            className="toast__action"
            onClick={() => {
              toast.action?.onClick();
              onDismiss(toast.id);
            }}
          >
            {toast.action.label}
          </button>
        )}
      </div>
      <button type="button" className="toast__close" aria-label="ปิดการแจ้งเตือน" onClick={() => onDismiss(toast.id)}>
        <X size={16} strokeWidth={1.75} aria-hidden="true" />
      </button>
    </li>
  );
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>');
  return ctx;
}
